import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import api from '../utils/api';

interface PromotionalBanner {
  _id: string;
  title: string;
  subtitle?: string;
  isActive: boolean;
  backgroundColor: string;
  textColor: string;
  linkUrl?: string;
  linkText?: string;
  priority: number;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminPromotionalBanners: React.FC = () => {
  const [banners, setBanners] = useState<PromotionalBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<PromotionalBanner | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    isActive: true,
    backgroundColor: '#dc2626',
    textColor: '#ffffff',
    linkUrl: '',
    linkText: '',
    priority: 1,
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await api.get('/api/promotional-banners');
      setBanners(response.data);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const bannerData = {
        ...formData,
        startDate: formData.startDate ? new Date(formData.startDate) : undefined,
        endDate: formData.endDate ? new Date(formData.endDate) : undefined
      };

      if (editingBanner) {
        await api.put(`/api/promotional-banners/${editingBanner._id}`, bannerData);
      } else {
        await api.post('/api/promotional-banners', bannerData);
      }

      setShowForm(false);
      setEditingBanner(null);
      setFormData({
        title: '',
        subtitle: '',
        isActive: true,
        backgroundColor: '#dc2626',
        textColor: '#ffffff',
        linkUrl: '',
        linkText: '',
        priority: 1,
        startDate: '',
        endDate: ''
      });
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
    }
  };

  const handleEdit = (banner: PromotionalBanner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      subtitle: banner.subtitle || '',
      isActive: banner.isActive,
      backgroundColor: banner.backgroundColor,
      textColor: banner.textColor,
      linkUrl: banner.linkUrl || '',
      linkText: banner.linkText || '',
      priority: banner.priority,
      startDate: banner.startDate ? new Date(banner.startDate).toISOString().split('T')[0] : '',
      endDate: banner.endDate ? new Date(banner.endDate).toISOString().split('T')[0] : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الإعلان؟')) {
      try {
        await api.delete(`/api/promotional-banners/${id}`);
        fetchBanners();
      } catch (error) {
        console.error('Error deleting banner:', error);
      }
    }
  };

  const toggleActive = async (banner: PromotionalBanner) => {
    try {
      await api.put(`/api/promotional-banners/${banner._id}`, {
        ...banner,
        isActive: !banner.isActive
      });
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-dark-hover rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 dark:bg-dark-hover rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-dark-bg dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg min-h-screen transition-colors duration-300">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">إدارة الإعلانات الترويجية</h1>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingBanner(null);
            setFormData({
              title: '',
              subtitle: '',
              isActive: true,
              backgroundColor: '#dc2626',
              textColor: '#ffffff',
              linkUrl: '',
              linkText: '',
              priority: 1,
              startDate: '',
              endDate: ''
            });
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>إضافة إعلان جديد</span>
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-surface rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-dark-text">
              {editingBanner ? 'تعديل الإعلان' : 'إضافة إعلان جديد'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                  العنوان الرئيسي *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                  العنوان الفرعي
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                    لون الخلفية
                  </label>
                  <input
                    type="color"
                    value={formData.backgroundColor}
                    onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                    className="w-full h-10 border border-gray-300 dark:border-dark-border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                    لون النص
                  </label>
                  <input
                    type="color"
                    value={formData.textColor}
                    onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                    className="w-full h-10 border border-gray-300 dark:border-dark-border rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                    رابط الإعلان
                  </label>
                  <input
                    type="url"
                    value={formData.linkUrl}
                    onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                    نص الرابط
                  </label>
                  <input
                    type="text"
                    value={formData.linkText}
                    onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                    الأولوية
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                    min="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                    تاريخ البداية
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-textSecondary mb-2">
                    تاريخ النهاية
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-dark-hover text-gray-900 dark:text-dark-text"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700 dark:text-dark-textSecondary">
                  نشط
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-gray-600 dark:text-dark-textSecondary hover:text-gray-800 dark:hover:text-gray-200"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
                >
                  {editingBanner ? 'تحديث' : 'إضافة'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Banners List */}
      <div className="space-y-4">
        {banners.map((banner) => (
          <div
            key={banner._id}
            className="bg-white dark:bg-dark-surface rounded-lg shadow-lg border border-gray-200 dark:border-dark-border overflow-hidden"
          >
            <div
              className="p-4 text-center"
              style={{
                backgroundColor: banner.backgroundColor,
                color: banner.textColor
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold">{banner.title}</h3>
                  {banner.subtitle && (
                    <p className="text-sm opacity-90 mt-1">{banner.subtitle}</p>
                  )}
                  {banner.linkUrl && banner.linkText && (
                    <a
                      href={banner.linkUrl}
                      className="text-sm underline hover:no-underline mt-1 inline-block"
                      style={{ color: banner.textColor }}
                    >
                      {banner.linkText}
                    </a>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs bg-white/20 px-2 py-1 rounded">
                    الأولوية: {banner.priority}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    banner.isActive ? 'bg-green-500/20' : 'bg-red-500/20'
                  }`}>
                    {banner.isActive ? 'نشط' : 'غير نشط'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-gray-50 dark:bg-dark-hover">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600 dark:text-dark-textSecondary">
                  <p>تاريخ الإنشاء: {new Date(banner.createdAt).toLocaleDateString('en-GB')}</p>
                  {banner.startDate && (
                    <p>تاريخ البداية: {new Date(banner.startDate).toLocaleDateString('en-GB')}</p>
                  )}
                  {banner.endDate && (
                    <p>تاريخ النهاية: {new Date(banner.endDate).toLocaleDateString('en-GB')}</p>
                  )}
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => toggleActive(banner)}
                    className={`p-2 rounded-lg ${
                      banner.isActive
                        ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20'
                        : 'text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    {banner.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  
                  <button
                    onClick={() => handleEdit(banner)}
                    className="p-2 text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/20 rounded-lg"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDelete(banner._id)}
                    className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPromotionalBanners;


